import { Box, Typography } from '@mui/material';
import { useSnackbar } from 'notistack';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink, useLocation, useSearchParams } from 'react-router';
import { ForbiddenError, UnauthorizedError } from 'src/api/APIClient';
import { useAPI } from 'src/api/provider';
import { API_HOST } from 'src/constants/environment';
import { REFERRER_STORAGE_KEY } from 'src/constants/sessionStorage';
import { Button } from 'src/lib/components/Button';
import { safeSessionStorage } from 'src/lib/browserStorage';
import { Link } from 'src/lib/components/Link';
import { TextField } from 'src/lib/components/fields/TextField';
import useForm from 'src/lib/components/form/useForm';
import { z } from 'zod/mini';
import Container from '../components/Container';
import { MagicLinkSent } from './MagicLinkSent';
import { getMCPMagicLinkReturnPath } from '../mcpOAuthContinuation';

interface FormValues {
    username: string;
}
interface MagicLinkFormProps {
    path: string;
    onRequestMagicLink(c: boolean): void;
    initialUsername: string;
}

export const MagicLinkForm = ({
    path,
    onRequestMagicLink,
    initialUsername,
}: MagicLinkFormProps) => {
    const { t } = useTranslation('auth.magicLink');
    const { t: tValidation } = useTranslation('validation');

    const api = useAPI();
    const { enqueueSnackbar } = useSnackbar();

    const form = useForm<FormValues>({
        validationSchema: z.object({
            username: z
                .string({
                    error: (iss) =>
                        iss.input === undefined ? tValidation('required') : tValidation('required'),
                })
                .check(z.minLength(1, tValidation('required'))),
        }),
        initialValues: {
            username: initialUsername,
        },
        onSubmit: async (values, { setErrors, setSubmitting }) => {
            try {
                await api.post('/magic-link-send/', {
                    path,
                    username: values.username,
                });
                onRequestMagicLink(false);
            } catch (err: any) {
                if (err instanceof UnauthorizedError || err instanceof ForbiddenError) {
                    setErrors({ username: err.message });
                } else {
                    enqueueSnackbar(err.message, { variant: 'error' });
                }
            } finally {
                setSubmitting(false);
            }
        },
    });

    const errors = form.submitCount > 0 ? form.errors : {};

    return (
        <form onSubmit={form.handleSubmit}>
            <Box marginBottom={4}>
                <TextField
                    label={t('usernameLabel')}
                    name="username"
                    autoComplete="username"
                    value={form.values.username}
                    onChange={(event) => {
                        const username = event.target.value;
                        form.setFieldValue('username', username);
                    }}
                    error={errors.username}
                    autoFocus
                />
            </Box>
            <Box marginBottom={2}>
                <Button
                    type="submit"
                    kind="filled"
                    color="primary"
                    fullWidth
                    disabled={form.isSubmitting}
                >
                    {t('sendLoginLinkButton')}
                </Button>
            </Box>
        </form>
    );
};

export const MagicLink = () => {
    const { t } = useTranslation('auth.magicLink');
    const [searchParams] = useSearchParams();

    const location = useLocation();
    const referrer = location.state?.referrer ?? null;
    const storedReferrerLink = safeSessionStorage.getItem(REFERRER_STORAGE_KEY);
    const referrerLink = (referrer && referrer.pathname + referrer.search) || storedReferrerLink;
    const magicLinkReturnPath = getMCPMagicLinkReturnPath({
        search: location.search,
        apiHost: API_HOST,
        getItem: safeSessionStorage.getItem,
        nowMs: Date.now(),
    });

    const [requestMagicLink, setRequestMagicLink] = useState<boolean>(true);

    if (!requestMagicLink) {
        return <MagicLinkSent onRequestMagicLink={setRequestMagicLink} />;
    }

    return (
        <Container width={360}>
            <Typography variant="h2" textAlign="center" marginBottom={2}>
                {t('loginLinkTitle')}
            </Typography>
            <Typography variant="body1" textAlign="center" marginBottom={4}>
                {t('loginLinkInfo')}
            </Typography>
            <MagicLinkForm
                path={magicLinkReturnPath || referrerLink || ''}
                onRequestMagicLink={setRequestMagicLink}
                initialUsername={searchParams.get('email') || ''}
            />
            <Box marginBottom={2}>
                <RouterLink to="/login">
                    <Button kind="outlined" color="primary" fullWidth>
                        {t('returnToLogin')}
                    </Button>
                </RouterLink>
            </Box>
            <Typography variant="body3" textAlign="center">
                <Link to="/sso">{t('loginWithSSO')}</Link>
            </Typography>
        </Container>
    );
};
